/**
 * Vid2Frames - Core Frame Extraction Engine & ZIP Service (TypeScript)
 */

import JSZip from 'jszip';
import { ExtractedFrame, ExtractionOptions, PhotoExtractionOptions } from './types';

export class ExtractorEngine {

  /**
   * Seeks video element to specified timestamp in seconds.
   */
  public static seekVideoTo(videoElement: HTMLVideoElement, time: number): Promise<void> {
    return new Promise((resolve) => {
      const onSeeked = () => {
        videoElement.removeEventListener('seeked', onSeeked);
        resolve();
      };
      videoElement.addEventListener('seeked', onSeeked);
      videoElement.currentTime = time;
    });
  }

  /**
   * Main frame extraction runner.
   */
  public static async extractFrames(options: ExtractionOptions): Promise<ExtractedFrame[]> {
    const {
      videoElement,
      videoDuration,
      extractionMode,
      fpsValue,
      countValue,
      useTimeRange,
      startTime,
      endTime,
      outputFormat,
      jpgQuality,
      onProgress,
      onFrameExtracted
    } = options;

    const effStart = useTimeRange ? startTime : 0;
    const effEnd = useTimeRange ? endTime : videoDuration;
    let effDuration = effEnd - effStart;
    if (effDuration <= 0) effDuration = 0.1;

    let totalFrames = countValue;
    if (extractionMode === 'fps') {
      totalFrames = Math.max(2, Math.ceil(effDuration * fpsValue));
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas 2D context not available');
    }

    canvas.width = videoElement.videoWidth || 1920;
    canvas.height = videoElement.videoHeight || 1080;

    const mimeType = outputFormat === 'JPG' ? 'image/jpeg' : 'image/png';
    const quality = outputFormat === 'JPG' ? (jpgQuality / 100) : 1.0;
    const ext = outputFormat.toLowerCase();

    const frames: ExtractedFrame[] = [];

    for (let i = 0; i < totalFrames; i++) {
      let t = effStart;
      if (totalFrames > 1) {
        t = effStart + (i * effDuration / (totalFrames - 1));
      }
      if (t >= videoDuration) t = Math.max(0, videoDuration - 0.05);

      // Seek video to frame timestamp
      await this.seekVideoTo(videoElement, t);

      // Render frame to canvas
      ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

      // Convert canvas to Blob
      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, mimeType, quality));
      if (!blob) continue;

      const url = URL.createObjectURL(blob);
      const filename = `frame_${String(i + 1).padStart(4, '0')}.${ext}`;

      const frameItem: ExtractedFrame = { blob, url, filename };
      frames.push(frameItem);

      const percent = Math.round(((i + 1) / totalFrames) * 100);

      if (typeof onFrameExtracted === 'function') {
        onFrameExtracted(frameItem, frames.length, totalFrames);
      }

      if (typeof onProgress === 'function') {
        onProgress(percent);
      }
    }

    return frames;
  }

  /**
   * TikTok Photo Post Carousel Extraction Runner.
   * Converts each carousel photo into the specified format (JPG/PNG) & quality.
   */
  public static async extractPhotos(options: PhotoExtractionOptions): Promise<ExtractedFrame[]> {
    const { photoUrls, outputFormat, jpgQuality, onProgress, onFrameExtracted } = options;
    const frames: ExtractedFrame[] = [];
    const totalCount = photoUrls.length;
    const mimeType = outputFormat === 'JPG' ? 'image/jpeg' : 'image/png';
    const quality = outputFormat === 'JPG' ? (jpgQuality / 100) : 1.0;
    const ext = outputFormat.toLowerCase();

    for (let i = 0; i < totalCount; i++) {
      const origUrl = photoUrls[i];
      let blob: Blob | null = null;

      try {
        blob = await this.fetchAndConvertImage(origUrl, mimeType, quality);
      } catch (err) {
        console.warn(`Failed to process photo #${i + 1}:`, err);
      }

      if (!blob) {
        // Fallback: fetch blob directly
        try {
          const resp = await fetch(origUrl.includes('wsrv.nl') ? origUrl : `https://wsrv.nl/?url=${encodeURIComponent(origUrl)}`);
          blob = await resp.blob();
        } catch (e) {
          console.error(`Fallback fetch failed for photo #${i + 1}`, e);
        }
      }

      if (blob) {
        const url = URL.createObjectURL(blob);
        const filename = `photo_${String(i + 1).padStart(4, '0')}.${ext}`;
        const frameItem: ExtractedFrame = { blob, url, filename };
        frames.push(frameItem);

        if (typeof onFrameExtracted === 'function') {
          onFrameExtracted(frameItem, frames.length, totalCount);
        }
      }

      const percent = Math.round(((i + 1) / totalCount) * 100);
      if (typeof onProgress === 'function') {
        onProgress(percent);
      }
    }

    return frames;
  }

  private static fetchAndConvertImage(imgUrl: string, mimeType: string, quality: number): Promise<Blob | null> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || img.width || 1080;
          canvas.height = img.naturalHeight || img.height || 1920;
          const ctx = canvas.getContext('2d');
          if (!ctx) return resolve(null);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(b => resolve(b), mimeType, quality);
        } catch (err) {
          reject(err);
        }
      };

      img.onerror = () => {
        // Retry with wsrv.nl proxy if direct fails
        if (!imgUrl.includes('wsrv.nl')) {
          const proxyUrl = `https://wsrv.nl/?url=${encodeURIComponent(imgUrl)}`;
          const retryImg = new Image();
          retryImg.crossOrigin = 'anonymous';
          retryImg.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = retryImg.naturalWidth || retryImg.width || 1080;
            canvas.height = retryImg.naturalHeight || retryImg.height || 1920;
            const ctx = canvas.getContext('2d');
            if (!ctx) return resolve(null);
            ctx.drawImage(retryImg, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(b => resolve(b), mimeType, quality);
          };
          retryImg.onerror = () => resolve(null);
          retryImg.src = proxyUrl;
        } else {
          resolve(null);
        }
      };

      const targetSrc = imgUrl.startsWith('data:') || imgUrl.includes('wsrv.nl')
        ? imgUrl
        : `https://wsrv.nl/?url=${encodeURIComponent(imgUrl)}`;
      img.src = targetSrc;
    });
  }

  /**
   * Bundles frame blobs into a ZIP archive and triggers automatic browser download.
   */
  public static async downloadZip(frames: ExtractedFrame[], zipNamePrefix = 'Vid2Frames_Export'): Promise<void> {
    if (!frames || frames.length === 0) return;

    try {
      const zip = new JSZip();
      frames.forEach(f => zip.file(f.filename, f.blob));
      const zipContent = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(zipContent);
      a.download = `${zipNamePrefix}_${Date.now()}.zip`;
      a.click();
    } catch (err) {
      console.error('JSZip export error:', err);
      this.downloadAllIndividual(frames);
    }
  }

  /**
   * Saves all frames individually to default downloads folder.
   */
  public static downloadAllIndividual(frames: ExtractedFrame[]): void {
    if (!frames || frames.length === 0) return;
    frames.forEach(f => {
      const a = document.createElement('a');
      a.href = f.url;
      a.download = f.filename;
      a.click();
    });
  }
}
